import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, Globe } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SUPPORTED_LANGS, setLanguage } from "@/i18n";

export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGS.find((l) => l.code === (i18n.language?.split("-")[0])) || SUPPORTED_LANGS[0];

  const choose = async (code) => {
    if (code === current.code) {
      setOpen(false);
      return;
    }
    setPending(code);
    setLanguage(code);
    setOpen(false);
    // Persist to backend if authenticated
    if (user) {
      try {
        await api.patch("/users/me/language", { language: code });
      } catch {
        /* ignore */
      }
    }
    setPending(null);
    // Soft reload to refetch translated content across all pages
    window.location.reload();
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="lang-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/15 ${
          compact ? "px-2.5 py-1" : "px-3 py-1.5"
        }`}
        style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
        aria-label="Change language"
      >
        <span className="text-base leading-none" aria-hidden>
          {current.flag}
        </span>
        {!compact && (
          <span className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
            {current.code}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 text-zinc-500 ${open ? "rotate-180" : ""}`} style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
      </button>

      {open && (
        <div
          data-testid="lang-switcher-dropdown"
          className="absolute right-0 mt-2 w-52 em-card p-2 animate-fade-in z-50"
          style={{ boxShadow: "0 20px 60px -20px rgba(0,0,0,0.7)" }}
        >
          <div className="px-3 py-2 border-b border-white/5 mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">
            <Globe className="w-3 h-3" /> Language
          </div>
          {SUPPORTED_LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => choose(l.code)}
              disabled={pending === l.code}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                l.code === current.code ? "text-gold bg-white/5" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`}
              style={{ transitionProperty: "background-color, color", transitionDuration: "160ms" }}
            >
              <span className="text-lg leading-none" aria-hidden>{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {l.code === current.code && <Check className="w-3.5 h-3.5 text-gold" strokeWidth={2} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
